<div class="general-settings d-sm-flex flex-column" [formGroup]="settingsForm">
    <h3>Product Feed Settings</h3>
    <!-- Crawl Type Selection -->
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Crawl Type</mat-label>
        <mat-select formControlName="type">
            @for (type of crawlTypes; track type) {
                <mat-option [value]="type">
                    {{ type }}
                </mat-option>
            }
        </mat-select>
    </mat-form-field>

    <section class="feed-graph-settings">
        <h4>Feed graph</h4>
        <p class="field-help">
            Standalone feeds publish their own products. A detail feed enriches the selected XML source feed and publishes into the source feed scope.
        </p>
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>XML source feed</mat-label>
            <mat-select formControlName="sourceFeedId"
                        (selectionChange)="onSourceFeedChange($event.value)">
                <mat-option value="">None — root or standalone feed</mat-option>
                @for (feed of availableSourceFeeds; track feed.id) {
                    <mat-option [value]="feed.id">
                        {{ feed.name || feed.id }}@if (feed.market) { ({{ feed.market }}) }
                    </mat-option>
                }
            </mat-select>
            @if (availableSourceFeeds.length === 0 && !isAssociatedDetailFeed) {
                <mat-hint>No eligible XML root feeds are available.</mat-hint>
            }
        </mat-form-field>

        @if (isAssociatedDetailFeed) {
            <div class="detail-feed-notice" role="status">
                This feed must remain JSON, skip page collection, use at least one allowed detail host, and include an executable <code>*</code> transformation.
            </div>
            <div formArrayName="detailHostAllowlist" class="detail-host-list">
                @for (host of detailHostAllowlist.controls; track host; let i = $index) {
                    <div class="detail-host-row">
                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Allowed detail host</mat-label>
                            <input matInput [formControlName]="i" placeholder="api.example.com">
                            @if (host.invalid && (host.dirty || host.touched)) {
                                <mat-error>{{ detailHostError(i) }}</mat-error>
                            }
                        </mat-form-field>
                        <button (click)="removeDetailHost(i)" mat-icon-button type="button"
                                aria-label="Remove allowed detail host">
                            <mat-icon>delete</mat-icon>
                        </button>
                    </div>
                }
                <button (click)="addDetailHost()" color="primary" mat-stroked-button type="button">
                    Add allowed detail host
                </button>
            </div>
            @if (settingsForm.hasError('invalidDetailFeedGraph') && settingsForm.touched) {
                <p class="graph-error" role="alert">Detail feeds require JSON type, skipped page collection, and an allowed detail host.</p>
            }
        }
    </section>

    <!-- Skip for Page Collection Toggle -->
    <mat-slide-toggle formControlName="skipForPageCollection">
        Skip for Page Collection
    </mat-slide-toggle>

    <!-- URL Field -->
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>URL</mat-label>
        <input matInput formControlName="url" placeholder="Enter URL">
    </mat-form-field>

    @if (!isAssociatedDetailFeed) {
        <!-- Default Domain Field -->
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Default Domain</mat-label>
            <input matInput formControlName="defaultDomain" placeholder="Enter default domain">
            @if (settingsForm.hasError('rootDefaultDomainRequired') && settingsForm.touched) {
                <mat-error>Default domain is required for a root or standalone feed.</mat-error>
            }
        </mat-form-field>

    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Brand Domain</mat-label>
        <input matInput formControlName="brandDomain" placeholder="karitraa.com">
    </mat-form-field>

    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Market</mat-label>
        <input matInput formControlName="market" placeholder="Norway, Sweden, Germany">
    </mat-form-field>

    <mat-form-field appearance="fill"
                    class="full-width"
                    subscriptSizing="dynamic"
                    data-testid="product-search-progress-field">
        <mat-label>Product search wait text</mat-label>
        <input matInput formControlName="productSearchProgressText"
               maxlength="160"
               placeholder="Leave blank to use locale default">
        @if (productSearchProgressTextHasValidationError) {
            <mat-error>
                {{ productSearchProgressTextValidationError }}
            </mat-error>
        }
        <mat-hint data-testid="product-search-progress-hint"
                  style="display: flex; width: 100%; min-width: 0; flex: 1 1 100%; flex-wrap: wrap; column-gap: 16px; row-gap: 2px;">
            <span>Displayed during product search. Max 160 plain-text characters.</span>
            <span data-testid="product-search-progress-counter"
                  style="margin-inline-start: auto; white-space: nowrap;">
                {{ productSearchProgressTextLength }} / 160
            </span>
        </mat-hint>
    </mat-form-field>
    <p class="progress-text-preview"
       data-testid="product-search-progress-preview"
       style="max-width: 100%; min-width: 0; overflow-wrap: anywhere; word-break: break-word;">
        Preview: {{ productSearchProgressTextPreview || 'Searching products ...' }}
    </p>

    <mat-slide-toggle formControlName="isDefaultFeed">
        Default feed for assistant fallback
    </mat-slide-toggle>

        <mat-accordion>
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Route rules
                </mat-panel-title>
            </mat-expansion-panel-header>
            <p class="field-help">
                Runtime evaluates route matches before market and Default feed fallbacks. Higher priority wins first;
                equal priority favors the more specific domain and path. Cross-feed ties use requested market, then
                Default feed; unresolved ties remain ambiguous and include every tied root.
            </p>
            <div class="routing-preview">
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Routing preview URL</mat-label>
                    <input matInput [formControl]="routePreviewUrl" placeholder="https://www.example.com/no/products/jacket">
                </mat-form-field>
                <p class="routing-preview__result routing-preview__result--{{ routingPreview.status }}"
                   aria-live="polite" data-testid="routing-preview-result">
                    {{ routingPreview.summary }}
                </p>
            </div>
            <div class="d-sm-flex flex-column" formArrayName="routeRules">
                @for (rule of routeRules.controls; track rule; let i = $index) {
                    <div class="d-sm-flex gap-16 align-items-center" [formGroupName]="i">
                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Domain pattern</mat-label>
                            <input matInput formControlName="domainPattern" placeholder="sweetprotection.com or *">
                        </mat-form-field>
                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Path pattern</mat-label>
                            <input matInput formControlName="pathPattern" placeholder="/no/*">
                        </mat-form-field>
                        <mat-form-field appearance="fill">
                            <mat-label>Priority</mat-label>
                            <input matInput formControlName="priority" type="number">
                        </mat-form-field>
                        <button (click)="removeRouteRule(i)" mat-icon-button type="button">
                            <mat-icon>delete</mat-icon>
                        </button>
                    </div>
                }
                <button (click)="addRouteRule()" color="primary" mat-stroked-button type="button">
                    Add route rule
                </button>
            </div>
        </mat-expansion-panel>
        </mat-accordion>
    }

    <mat-accordion>
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Data configuration
                </mat-panel-title>
            </mat-expansion-panel-header>
            <div class="d-sm-flex flex-column">
                <!-- Listings Directory Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Listings Directory</mat-label>
                    <input matInput formControlName="listingsDirectory" placeholder="Enter listings directory">
                </mat-form-field>

                <!-- Strip Namespace Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Strip Namespace</mat-label>
                    <input matInput formControlName="stripNamespace" placeholder="Enter strip namespace">
                </mat-form-field>

                <!-- URL Property Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>URL Property</mat-label>
                    <input matInput formControlName="urlProperty" placeholder="Enter URL property">
                </mat-form-field>

                <!-- Crawl Regex Pattern Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Crawl Regex Pattern</mat-label>
                    <input matInput formControlName="crawlRegexPattern" placeholder="Enter crawl regex pattern">
                </mat-form-field>

                <!-- URL Regex Pattern Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>URL Regex Pattern</mat-label>
                    <input matInput formControlName="urlRegexPattern" placeholder="Enter URL regex pattern">
                </mat-form-field>

                <!-- URL Regex Replace Pattern Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>URL Regex Replace Pattern</mat-label>
                    <input formControlName="urlRegexReplacePattern" matInput
                           placeholder="Enter URL regex replace pattern">
                </mat-form-field>

                <!-- Custom Category Syntax Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Custom Category Syntax</mat-label>
                    <input matInput formControlName="customCategorySyntax" placeholder="Enter custom category syntax">
                </mat-form-field>
            </div>
        </mat-expansion-panel>
    </mat-accordion>
</div>
